<template>
  <Line
    id="my-chart-id"
    :options="chartOptions"
    :data="chartData"
    ref="myLineChart"
  />
</template>

<script>
import 'chartjs-adapter-moment'
import {Line} from 'vue-chartjs'
import {Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, TimeScale, PointElement, LineElement} from 'chart.js'
import ChartHelper from "./ChartHelper"

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, TimeScale, PointElement, LineElement)

export default {
  name: 'LineChart',
  props: ['chartType', 'queryColumns', 'queryResult', 'chartZoomResetKey', 'queryID'],

  components: {Line, CategoryScale, LinearScale, TimeScale},
  /*
    watch: {
      chartZoomResetKey() {
        this.$refs[this.queryID].chart.resetZoom()
      }
    },
  */
  data() {
    return {
      NUMBER_CFG: {count: 7, min: 0, max: 100},
      selDataRowIdx: null,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        },
        plugins: {
          title: {
            display: false,
            text: 'Stacked scales',
            padding:0
          },
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 10,
              font: {
                size: 10
              }
            }
          },
          zoom: {/*
              zoom: {
                wheel: { enabled: true },
                pinch: { enabled: true },
                mode: 'xy',
              },
              pan: {
                enabled: true,
                mode: 'x',
                threshold: 3
              },*/
          }
        },
        elements: {
          point: {
            radius: this.radius,
            display: true
          }
        },
        scales: {}
      }
    }
  },

  computed: {
    chartOptions() {
      let steppedAxisTemplateList = {}

      // Volt TRU, T&T
      steppedAxisTemplateList["li"] = {
        type: 'linear',
        position: 'left',
        stack: 'demo',
        offset: false,
        stackWeight: 1,
        border: {
          color: 'blue'
        },
        title: {
          display: true,
          text: 'V/RPM',
          font: {size: 11}
        }
      }


/*
      steppedAxisTemplateList["oc"] = {
        type: 'linear',
        position: 'left',
        stack: 'demo',
        offset: true,
        stackWeight: 1,
        border: {
          color: 'blue'
        },
        title: {
          display: true,
          text: 'Door',
          font: {size: 11}
        }
      }
*/

      steppedAxisTemplateList["ls"] = {
        type: 'category',
        labels: ['Open', 'Unknown', 'Close'],
        offset: true,
        position: 'left',
        stack: 'demo',
        stackWeight: 1,
        border: {
          color: 'orange'
        },
        title: {
          display: true,
          text: 'DOOR',
          font: {size: 11}
        }
      }

      // power, couple
      steppedAxisTemplateList["oo"] = {
        type: 'category',
        labels: ['Off', 'On', 'Unknown'],
        offset: false,
        position: 'left',
        stack: 'demo',
        stackWeight: 1.5,
        border: {
          color: 'red'
        },
        title: {
          display: true,
          text: 'PWR',
          font: {size: 11}
        }
      }

      // operation mode
      steppedAxisTemplateList["op"] = {
        type: 'category',
        labels: ['heat', 'cool', 'defrost', 'other', 'shutdown', 'pretrip', 'off'],
        offset: false,
        position: 'left',
        stack: 'demo',
        stackWeight: 2,
        ticks: {
          autoSkip: false
        },
        border: {
          color: 'yellow'
        },
        title: {
          display: true,
          text: 'OPM',
          font: {size: 11}
        },
      }

      steppedAxisTemplateList["es"] = {
        type: 'category',
        i1_name: 'es', // engine states
        labels: ['Continues', 'Start|Stop', 'Cycle sentry', 'Sleep', 'Off'],
        offset: true,
        position: 'left',
        stack: 'demo',
        stackWeight: 1,
        border: {
          color: 'green'
        },
        title: {
          display: true,
          text: 'ENGINE',
          font: {size: 11}
        }
      }

      if (this.queryResult.length > 0) {
        Object.keys(this.queryResult[0]).forEach(colName => {
          let firstValue = this.queryResult[0][colName]
          if (firstValue && firstValue.indexOf("::") !== -1) {
            let axis = firstValue.split('::')[0]
            let count = axis.slice(-1)
            let templName = axis

            if (!isNaN(count)) {
              templName = axis.substring(0, 2)
            }

            if (steppedAxisTemplateList[templName]) {
              // Tatsächlich vorhandene Werte aus ALLEN Datensätzen sammeln
              let usedLabels = new Set()
              this.queryResult.forEach(row => {
                let val = row[colName]
                if (val && val.indexOf("::") !== -1) {
                  usedLabels.add(val.split('::')[1].toLowerCase())
                }
              })

              // Template kopieren und Labels filtern (Original-Reihenfolge beibehalten)
              let axisConfig = {...steppedAxisTemplateList[templName]}
              if (axisConfig.labels) {
                axisConfig.labels = axisConfig.labels.filter(label => usedLabels.has(label.toLowerCase()))

                // Leeres Label oben als Abstandshalter zur nächsten Achse
                axisConfig.labels = ['', ...axisConfig.labels]

                // stackWeight dynamisch: pro Label ca. 0.2 Gewicht, mindestens 1
                axisConfig.stackWeight = Math.max(1, axisConfig.labels.length * 0.2)
              }
              // Achse IMMER registrieren – auch ohne Labels (z.B. für 'linear')
              this.options.scales[axis] = axisConfig
            }
          }
        })
      }

      this.options.scales.default = {
        type: 'linear',
        position: 'left',
        stack: 'demo',
        stackWeight: 3,
        border: {
          color: 'blue'
        },
        offset: false,

      }

      // shot click event ::EVENT
      this.options.onClick = (event, elements) => {
        if (elements.length > 0) {
          const dataIndex = elements[0].index
          const row = this.queryResult[dataIndex]
          for (let i = 0; i < this.queryColumns.length; i++) {
            if (this.queryColumns[i].chartIsLegend) {
              this.eventBus.queryRowSel(this.queryID, this.queryColumns[i], Object.values(row)[i])
              break
            }
          }
        }
      }

      if (this.queryColumns && this.queryColumns.length > 0) {
        this.queryColumns.forEach((col) => {
          switch (col.datatypeID) {
            case 21: // DateTime
            case 22: // Date
              this.options.scales.x = {
                type: 'time',
                time: {
                  displayFormats: {
                    day: 'DD.MM.YYYY',
                    hour: 'DD.MM.YYYY HH:mm',
                    minute: 'HH:mm',
                  },
                  tooltipFormat: 'DD.MM.YYYY HH:mm'
                }
              }
              break

            case 23: // Time
              this.options.scales.x = {
                type: 'time',
                time: {
                  unit: 'hour',
                  stepSize: 1,
                  tooltipFormat: 'HH:mm',
                  displayFormats: {
                    'millisecond': 'HH:mm',
                    'second': 'HH:mm',
                    'minute': 'HH:mm',
                    'hour': 'HH:mm',
                    'day': 'HH:mm',
                    'week': 'HH:mm',
                    'month': 'HH:mm',
                    'quarter': 'HH:mm',
                    'year': 'HH:mm',
                  },
                }
              }
              break
          }
        })
      }

      return this.options
    },


    chartData() {
      if (this.queryResult && this.queryResult.length > 0) {
        let datasets = ChartHelper.buildDataset(this.chartType, this.queryColumns, this.queryResult)
        // logger.info("Datasets:", datasets, ", len:", datasets.labels.length)

        return datasets
      }
    }
  },

  methods: {
    radius(context) {
      // logger.info("radius. ", context.dataIndex, context.raw.x, "==", this.selDataRowIdx)
      let width = this.$refs.myLineChart?.chart?.width
      let isMaximized = false
      if (width && width >= 800) isMaximized = true

      if (context.raw.x === this.selDataRowIdx) {
        if (isMaximized) return 6
        else return 4
      } else {
        // logger.info("radius. ", context.dataIndex, context.raw.x, "==", this.selDataRowIdx)
        if (isMaximized) return 2
        return 1
      }
    },

    isDateFormatValid(dateString) {
      var date = new Date(dateString);
      return !isNaN(date.getTime());
    },

    queryRowSelEventReceiver({senderQueryID, col, value}) {
      // logger.info("LINECHART - queryRowSelEventReceiver.", col, value)
      // logger.info("LINECHART - this.queryResult.", this.queryResult)

      for (let i = 0; i < this.queryColumns.length; i++) {
        if (this.queryColumns[i].nullValueReplacement && this.queryColumns[i].nullValueReplacement.includes("::EVENT_RECEIVER")) {

          for (let r = 0; r < this.queryResult.length; r++) {

            if (Object.values(this.queryResult[r])[i] === value) {
              // logger.info(Object.values(this.queryResult[r])[i])

              // this.selDataRowIdx = r
              this.selDataRowIdx = Object.values(this.queryResult[r])[i]

              let chartInstance = this.$refs.myLineChart.chart
              chartInstance.update()

              break
            }
          }
          break
        }
      }
    }
  },

  beforeUnmount: function () {
    this.eventBus.off("onQueryRowSel", this.queryRowSelEventReceiver)
  },

  mounted: function () {
    logger.info("Mount Linechart!")
    this.eventBus.on("onQueryRowSel", this.queryRowSelEventReceiver)
  }

}
</script>
